import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import {
  createShivasutra,
  deleteShivasutra,
  getShivasutra,
  listShivasutras,
  updateShivasutra,
  type Shivasutra,
} from '../api/shivasutraApi';

type FormValues = {
  sutraOrder: number;
  sutra: string;
  kashika: string;
  vyakhya: string;
};

export function ShivaSutraPage() {
  const [rows, setRows] = useState<Shivasutra[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Shivasutra | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Shivasutra | null>(null);
  const [form] = Form.useForm<FormValues>();
  /** Values to apply after Modal + Form mount (destroyOnClose); avoids empty fields. */
  const formSeedRef = useRef<FormValues | null>(null);
  const modalModeRef = useRef<'create' | 'edit'>('create');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listShivasutras();
      setRows(data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load Shiva Sutras');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    modalModeRef.current = 'create';
    formSeedRef.current = null;
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = async (record: Shivasutra) => {
    try {
      const full = await getShivasutra(record.id);
      modalModeRef.current = 'edit';
      formSeedRef.current = {
        sutraOrder: full.sutraOrder,
        sutra: full.sutra ?? '',
        kashika: full.kashika ?? '',
        vyakhya: full.vyakhya ?? '',
      };
      setEditing(full);
      setModalOpen(true);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load Shiva Sutra for edit');
    }
  };

  const closeModal = () => {
    formSeedRef.current = null;
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const syncFormAfterModalOpen = (open: boolean) => {
    if (!open) return;
    const apply = () => {
      const seed = formSeedRef.current;
      if (seed) {
        form.setFieldsValue(seed);
        formSeedRef.current = null;
        return;
      }
      if (modalModeRef.current === 'create') {
        form.resetFields();
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(apply);
    });
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        const patch: Partial<FormValues> = {};
        if (values.sutraOrder !== editing.sutraOrder) patch.sutraOrder = values.sutraOrder;
        if (values.sutra !== editing.sutra) patch.sutra = values.sutra;
        if (values.kashika !== editing.kashika) patch.kashika = values.kashika;
        if (values.vyakhya !== editing.vyakhya) patch.vyakhya = values.vyakhya;
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateShivasutra(editing.id, patch);
        message.success('Shiva Sutra updated');
      } else {
        await createShivasutra({
          sutraOrder: values.sutraOrder,
          sutra: values.sutra.trim(),
          kashika: values.kashika.trim(),
          vyakhya: values.vyakhya.trim(),
        });
        message.success('Shiva Sutra created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      const row = await getShivasutra(id);
      setDetail(row);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load Shiva Sutra');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteShivasutra(id);
      message.success('Shiva Sutra deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<Shivasutra> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Order', dataIndex: 'sutraOrder', width: 88, sorter: (a, b) => a.sutraOrder - b.sutraOrder },
    { title: 'Sūtra', dataIndex: 'sutra', ellipsis: true },
    { title: 'Kāśikā', dataIndex: 'kashika', ellipsis: true },
    { title: 'Vyākhyā', dataIndex: 'vyakhya', ellipsis: true },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openDetail(record.id)}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => void openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this Shiva Sutra?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => void handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Shiva Sutra"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Shiva Sutra
        </Button>
      }
    >
      <Table<Shivasutra>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 900 }}
      />

      <Modal
        title="Shiva Sutra details"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={null}
        width={640}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>
              <strong>ID:</strong> {detail.id}
            </Typography.Text>
            <Typography.Text>
              <strong>Order:</strong> {detail.sutraOrder}
            </Typography.Text>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              <strong>Sūtra:</strong> {detail.sutra}
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              <strong>Kāśikā:</strong> {detail.kashika}
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              <strong>Vyākhyā:</strong> {detail.vyakhya}
            </Typography.Paragraph>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={editing ? 'Edit Shiva Sutra' : 'Add Shiva Sutra'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        destroyOnClose
        afterOpenChange={syncFormAfterModalOpen}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="sutraOrder"
            label="Sūtra order (1–14)"
            rules={[
              { required: true, message: 'Order is required' },
              {
                type: 'number',
                min: 1,
                max: 14,
                message: 'Must be between 1 and 14',
              },
            ]}
          >
            <InputNumber min={1} max={14} style={{ width: '100%' }} placeholder="JSON id 1–14" />
          </Form.Item>
          <Form.Item name="sutra" label="Sūtra" rules={[{ required: true, message: 'Sūtra is required' }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="kashika" label="Kāśikā" rules={[{ required: true, message: 'Kāśikā is required' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="vyakhya" label="Vyākhyā" rules={[{ required: true, message: 'Vyākhyā is required' }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
